// Type definitions for license data
export interface LicenseData {
  FirstName: string
  LastName: string
  LicenseNumber: string
  ExpirationDate: string
  Address1: string
  Address2?: string
  City: string
  State: string
  Zip: string
  BirthDate?: string
  Gender?: string
}

/**
 * Utility function to detect mobile devices
 * @returns {boolean} True if the device is mobile
 */
export const isMobileDevice = (): boolean => {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0
  )
}

/**
 * Parse AAMVA barcode data from driver's license
 * @param {string} barcodeText - The raw barcode text data
 * @returns {Partial<LicenseData>} Parsed license data fields
 */
export const parseAAMVABarcode = (barcodeText: string): Partial<LicenseData> => {
  const fields: Partial<LicenseData> = {}

  // Common AAMVA field mappings
  const fieldMap: Record<string, keyof LicenseData> = {
    DAC: 'FirstName',
    DCS: 'LastName',
    DAQ: 'LicenseNumber',
    DBA: 'ExpirationDate',
    DAG: 'Address1',
    DAH: 'Address2',
    DAI: 'City',
    DAJ: 'State',
    DAK: 'Zip',
    DBB: 'BirthDate',
    DBC: 'Gender',
  }

  // Common AAMVA field codes found in real driver's licenses
  const commonAAMVACodes = [
    'DAC',
    'DCS',
    'DAQ',
    'DBA',
    'DAG',
    'DAH',
    'DAI',
    'DAJ',
    'DAK',
    'DBB',
    'DBC',
    'DAD',
    'DBD',
    'DCA',
    'DCB',
    'DCD',
    'DCF',
    'DCG',
    'DDD',
    'DAU',
  ].join('|')

  // More robust AAMVA parsing
  Object.entries(fieldMap).forEach(([code, fieldName]) => {
    // Use regex to find the field code and capture data until next AAMVA field code or delimiter
    const regex = new RegExp(`${code}([^\\r\\n]*?)(?=${commonAAMVACodes}|ZN|$)`, 'g')
    const match = regex.exec(barcodeText)

    if (match && match[1]) {
      let value = match[1]

      // More aggressive cleaning of AAMVA artifacts
      // Step 1: Remove all periods (common padding character)
      value = value.replace(/\.+/g, '')

      // Step 2: Remove common padding characters and artifacts
      value = value.replace(/[_\-~`]+/g, '') // Remove other common padding chars

      // Step 3: Normalize all whitespace to single spaces
      value = value.replace(/\s+/g, ' ')

      // Step 4: Remove leading/trailing whitespace
      value = value.trim()

      // Special field-specific cleaning
      if (fieldName === 'LicenseNumber') {
        // Extract only alphanumeric characters for license number
        value = value.replace(/[^A-Z0-9]/gi, '')
      }

      if (fieldName === 'Address1') {
        // Clean up address formatting but preserve meaningful structure
        value = value.replace(/\s{2,}/g, ' ') // Multiple spaces to single space
        // Keep common address abbreviations and numbers
        value = value.replace(/(\d+)\s+([A-Z])\s+(\d+)/g, '$1 $2 $3') // Fix "93 S 3" type patterns
      }

      // Handle date formatting
      if (fieldName === 'ExpirationDate' || fieldName === 'BirthDate') {
        // Clean and extract 8 digits
        const digits = value.replace(/[^\d]/g, '')
        if (digits.length === 8) {
          const month = digits.substring(0, 2)
          const day = digits.substring(2, 4)
          const year = digits.substring(4, 8)
          value = `${month}/${day}/${year}`
        }
      }

      // Handle gender codes (1 = Male, 2 = Female)
      if (fieldName === 'Gender') {
        const cleanGender = value.replace(/[^\d]/g, '')
        value = cleanGender === '1' ? 'M' : cleanGender === '2' ? 'F' : value.substring(0, 1).toUpperCase()
      }

      // Handle zip codes
      if (fieldName === 'Zip') {
        // Extract only digits
        const digits = value.replace(/[^\d]/g, '')

        if (digits.length >= 5) {
          // Take first 5 digits for zip, or 9 if it's a zip+4
          if (digits.length >= 9 && digits.substring(5, 9) !== '0000') {
            value = digits.substring(0, 5) + '-' + digits.substring(5, 9)
          } else {
            value = digits.substring(0, 5)
          }
        }
      }

      // Handle names - clean up extra spaces but preserve capitalization
      if (fieldName === 'FirstName' || fieldName === 'LastName') {
        value = value.replace(/\s+/g, ' ').trim()
        // Convert to proper case if all caps
        if (value === value.toUpperCase()) {
          value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
        }
      }

      // Only store if we have meaningful data
      if (value && value.length > 0) {
        fields[fieldName as keyof LicenseData] = value as any
      }
    }
  })

  return fields
}

/**
 * Validate if barcode data appears to be corrupted or incomplete
 * @param {string} barcodeText - The raw barcode text data
 * @returns {object} Validation result with status and suggestions
 */
export const validateBarcodeQuality = (
  barcodeText: string,
): {
  isValid: boolean
  issues: string[]
  suggestions: string[]
} => {
  const issues: string[] = []
  const suggestions: string[] = []

  // Check for excessive periods that might indicate corruption
  const periodCount = (barcodeText.match(/\./g) || []).length
  const totalLength = barcodeText.length
  const periodRatio = periodCount / totalLength

  if (periodRatio > 0.15) {
    // More than 15% periods is suspicious
    issues.push('Excessive periods detected - may indicate scan corruption')
    suggestions.push('Try scanning more slowly and ensure good lighting')
  }

  // Check for broken license number patterns
  const licenseMatch = barcodeText.match(/DAQ([^A-Z]*?)(?=[A-Z]{3}|ZN|$)/g)
  if (licenseMatch && licenseMatch[0]) {
    const licenseData = licenseMatch[0].replace('DAQ', '')
    if (licenseData.includes('..') || licenseData.match(/\.\s+\d/)) {
      issues.push('License number appears fragmented')
      suggestions.push('Rescan the barcode more carefully, or try photo scan mode')
    }
  }

  // Check for essential field presence
  const requiredFields = ['DAC', 'DCS', 'DAQ', 'DBA']
  const missingFields = requiredFields.filter(field => !barcodeText.includes(field))

  if (missingFields.length > 0) {
    issues.push(`Missing essential fields: ${missingFields.join(', ')}`)
    suggestions.push('Ensure you are scanning the complete barcode')
  }

  return {
    isValid: issues.length === 0,
    issues,
    suggestions,
  }
}
